import { createSlice, createDraftSafeSelector, PayloadAction } from '@reduxjs/toolkit';

interface IDirectorInfo {
  directorId: string;
}

interface DirectorState {
  directorList: IDirectorInfo[];
  directorInfo: IDirectorInfo;
  actionResult: string;
  isLoading: boolean;
  error: string | null;
}

const InitialState: DirectorState = {
  directorList: [],
  directorInfo: {},
  actionResult: '',
  isLoading: false,
  error: '',
};

const reducers = {
  stateClear: (state: DirectorState, { payload }: PayloadAction<DirectorState>) => {
    state.directorList = [];
    state.directorInfo = {};
    state.actionResult = '';
    state.isLoading = false;
    state.error = '';
  },
  list: (state: DirectorState, { payload }: PayloadAction<DirectorState>) => {
    state.isLoading = true;
    state.actionResult = 'LIST_REQ';
    state.error = '';
  },
  listSuccess: (state: DirectorState, { payload }: PayloadAction<DirectorState>) => {
    state.directorList = payload.directorList;
    state.isLoading = false;
    state.actionResult = 'LIST_OK';
    state.error = '';
  },
  listFailure: (state: DirectorState, { payload }: PayloadAction<DirectorState>) => {
    state.isLoading = false;
    state.actionResult = 'LIST_ERR';
    state.error = payload.error;
  },
  detail: (state: DirectorState, { payload }: PayloadAction<DirectorState>) => {
    state.isLoading = true;
    state.actionResult = 'DETAIL_REQ';
    state.error = '';
  },
  detailSuccess: (state: DirectorState, { payload }: PayloadAction<DirectorState>) => {
    state.directorInfo = payload.directorInfo;
    state.isLoading = false;
    state.actionResult = 'DETAIL_OK';
    state.error = '';
  },
  detailFailure: (state: DirectorState, { payload }: PayloadAction<DirectorState>) => {
    state.isLoading = false;
    state.actionResult = 'DETAIL_ERR';
    state.error = payload.error;
  },
  add: (state: DirectorState, { payload }: PayloadAction<CsutomerDirectorState>) => {
    state.isLoading = true;
    state.actionResult = 'ADD_REQ';
    state.error = '';
  },
  addSuccess: (state: DirectorState, { payload }: PayloadAction<DirectorState>) => {
    state.isLoading = false;
    state.actionResult = 'ADD_OK';
    state.error = '';
  },
  addFailure: (state: DirectorState, action: PayloadAction<string>) => {
    state.isLoading = false;
    state.actionResult = 'ADD_ERR';
    state.error = action.payload;
  },
  edit: (state: DirectorState, { payload }: PayloadAction<CsutomerDirectorState>) => {
    state.isLoading = true;
    state.actionResult = 'EDIT_REQ';
    state.error = '';
  },
  editSuccess: (state: DirectorState, { payload }: PayloadAction<DirectorState>) => {
    state.isLoading = false;
    state.actionResult = 'EDIT_OK';
    state.error = '';
  },
  editFailure: (state: DirectorState, action: PayloadAction<string>) => {
    state.isLoading = false;
    state.actionResult = 'EDIT_ERR';
    state.error = action.payload;
  },
  remove: (state: DirectorState, { payload }: PayloadAction<CsutomerDirectorState>) => {
    state.isLoading = true;
    state.actionResult = 'REMOVE_REQ';
    state.error = '';
  },
  removeSuccess: (state: DirectorState, { payload }: PayloadAction<DirectorState>) => {
    state.isLoading = false;
    state.actionResult = 'REMOVE_OK';
    state.error = '';
  },
  removeFailure: (state: DirectorState, action: PayloadAction<string>) => {
    state.isLoading = false;
    state.actionResult = 'REMOVE_ERR';
    state.error = action.payload;
  },
  actionResultClear: (state: DirectorState) => {
    state.actionResult = '';
  },
};

const slice = createSlice({
  name: 'director',
  initialState: InitialState,
  reducers: reducers,
});

const selectDirectorList = createDraftSafeSelector(
  (state: DirectorState) => state.directorList,
  (directorList) =>  [...directorList].sort((a,b) => new Date(b.inDt) - new Date(a.inDt)),
);

const selectDirectorInfo = createDraftSafeSelector(
  (state: DirectorState) => state.directorInfo,
  directorInfo => directorInfo,
);

const selectStatus = createDraftSafeSelector(
  (state: DirectorState) => state.actionResult,
  (state: DirectorState) => state.isLoading,
  (state: DirectorState) => state.error,
  (actionResult, isLoading, error) => ({
    actionResult,
    isLoading,
    error,
  }),
);

export const directorSelector = {
  directorList: state => selectDirectorList(state[DIRECTOR]),
  directorInfo: state => selectDirectorInfo(state[DIRECTOR]),
  status: state => selectStatus(state[DIRECTOR]),
};

export const DIRECTOR = slice.name;
export const directorReducer = slice.reducer;
export const directorAction = slice.actions;
